/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {SpreaderOptions} from '@ion/core/src/entities/Spreader';
import {translate} from '@ion/core/src/locale/locale';
import {Entity} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, noneOption, Options} from '../components/menus/Dropdown';

export type SpreaderChooserProps = {
  entity?: Entity;
  value?: SpreaderOptions;
  spreader?: SpreaderOptions;
  readOnly?: boolean;
  onChange?: (value) => void;
  required?: boolean;
  error?: string;
  classes?: string;
  defaultValue?: string;
  defaultLabel?: boolean;
  isRoleViewMode?: boolean;
  includeNoneLabel?: string;
  includeNone?: boolean;
  multiSelect?: boolean;
};

/**
 * Generates the spreader status chooser dropdown labels and values based on
 * the translated status entries.
 */
export const getOptions = (includeNoneLabel: string, includeNone = false): Options[] => {
  const entries  = translate('common:editors.spreaderOptions', {returnObjects: true});
  const options  = createOptions({
    items     : entries,
    labelProp : 'label',
    valProp   : 'value'
  });
  return noneOption({
    label : includeNoneLabel,
    value : 'None',
    includeNone,
    options
  });
};

/**
 * @SpreaderChooser
 *
 * The SpreaderChooser provides a list of localized CTC Entity Status values
 * (such as “Cirrus”, “Dickey John”, etc.) for display within a dropdown list and
 * allows the user to select a Status from the list which will be returned to the
 * client as the non-translated CTC Entity Status value.
 *
 * ```typescript
 *
 *     import {SpreaderChooser} from '@ion/dls/src/editors/';
 *     import {Entity} from '@ion/core/src/types/entities';
 *
 *     <SpreaderChooser status={DeviceFilelds.Features.ATTACHMENTS} onChange(setStatus) />
 *
 *     // read-only mode ...
 *     <SpreaderChooser readOnly
 *       entity={Entity.status}
 *       status={item.status}
 *       onChange(setStatus)
 *       />
 *
 *     <SpreaderChooser includeDelete
 *       status={item.status}
 *       onChange(setStatus)
 *       />
 *
 *     // provide an entity specific inline label ...
 *     <SpreaderChooser entity={Entity.asset} onChange(setStatus) />
 *
 *
 * @status        Allows for providing a default spreader status, generally used for
 *                edits.
 * @onChange      Callback prop which will be invoked with the seleted CTC spreader
 *                option value and selected label.
 * @entity        The context of the Spreaderchooser; i.e. the CTC Entity type
 *                for which the chooser is being displayed (used for determining the
 *                label to be displayed). If omitted, the inline label will not be
 *                displayed.
 * @defaultLabel  When true, default label "Status" will be displayed when entity is
 *                not defined. Defaults to false.
 * @readOnly      When true, the component will be displayed in read-only mode;
 *                that is, disabled. Defaults to false.
 */
export const SpreaderChooser = ({
  entity,
  value,
  readOnly,
  onChange,
  required,
  classes,
  isRoleViewMode,
  multiSelect = false,
  includeNone = true,
  defaultLabel = false,
  includeNoneLabel = translate('common:options.none')
}: SpreaderChooserProps) => {
  const [speaderval, setSpreaderval] = useState(
    (multiSelect ? [] : ((value || includeNone)) ? includeNoneLabel : undefined)
  );

  const dropdownOptions = useMemo(() => (
    getOptions(includeNoneLabel, !multiSelect && includeNone)
  ), [multiSelect, includeNone, includeNoneLabel]);
  // A default value is often set, we should
  // onChange if one exists on mount so the
  // consumer's formState can be updated if need be.
  useEffect(() => {
    if (value) {
      const selectedValue = multiSelect ? value.split(',') : value;
      setSpreaderval(selectedValue);
    }
  }, [multiSelect, value]);

  const handleOnChange = useCallback(({target: {value}}, selectedOptions = []) => {
    if (multiSelect) {
      const idValues: string[] = selectedOptions.map((child) => (typeof child === 'string' ? child : child.value));
      setSpreaderval(selectedOptions);
      onChange?.(idValues.join(','));
    } else {
      setSpreaderval(value);
      onChange?.(value);
    }
  }, [onChange]);

  return (
    <div className={classes}>
      <Dropdown required={required}
        labelAsValue={false}
        displaySelectedLabel
        multiSelect={multiSelect}
        disabled={readOnly || isRoleViewMode}
        value={speaderval}
        options={dropdownOptions}
        placeholder={translate('common:editors.spreader.placeholder')}
        fieldLabel={value ? translate('common:editors.spreader.label', {
          entity : translate(`common:entityLabels.${entity}`)
        }) : defaultLabel ? translate('common:editors.spreader.label') : undefined}
        onChange={handleOnChange} />
    </div>
  );
};

export default memo(SpreaderChooser);
